import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  SafeAreaView,
  StyleSheet,
  View,
  Text,
  FlatList,
  Pressable,
} from 'react-native';
import { Icon } from 'react-native-elements';
import dayjs from 'dayjs';
import { get } from 'lodash';

import SearchBar from '../../components/SearchBar';
import { BuildingSelectionProps } from '../../navigation';
import config from '../../lib/config';
import { RootState } from '../../store/rootReducer';

import { getNewsList, selectNews } from '../../store/newsSlice';
import { NewsItem } from '../../api/adserver';
import { showModal, hideModal } from '../../store/appSlice';
import { ModalName } from '../../components/RootOverlays';

type Props = {} & BuildingSelectionProps;

const NewsScreen: React.FC<Props> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const { contentId } = useMemo(() => {
    return {
      contentId: get(route, 'params.contentId', ''),
    };
  }, [route]);
  console.log(`NewsScreen, contentId: ${contentId}`);

  const data = useSelector((state) => selectNews(state as RootState).data);

  useEffect(() => {
    dispatch(getNewsList());
  }, [dispatch]);

  const handleOnClose = useCallback(() => {
    dispatch(hideModal());
  }, [dispatch]);

  const onViewDetail = useCallback(
    (item: NewsItem) => {
      dispatch(
        showModal({
          isShowModal: true,
          modalName: ModalName.NewItems,
          modalProps: {
            item,
            onClose: handleOnClose,
          },
        }),
      );
    },
    [dispatch, handleOnClose],
  );

  useEffect(() => {
    if (contentId) {
      const newsItem = data.find((item) => String(item.id) === contentId);
      if (newsItem) {
        onViewDetail(newsItem);
      }
    }
  }, [contentId, data, onViewDetail]);

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.searchWrapper}>
        <Icon
          tvParallaxProperties={null}
          name="arrow-back"
          size={30}
          color="#ffffff"
          onPress={navigation.goBack}
        />
        <View style={styles.searchBar}>
          <SearchBar placeholder="Search" />
        </View>
      </View>
      <View style={[styles.scrollView]}>
        <FlatList
          style={styles.newsList}
          data={data || []}
          renderItem={({ item }) => (
            <Pressable onPress={() => onViewDetail(item)}>
              <View style={styles.newsItem}>
                <View style={styles.content}>
                  <Text style={styles.date}>
                    {dayjs(item.updated_at).format('DD/MM/YYYY')}
                  </Text>
                  <Text style={styles.title}>{item.title}</Text>
                </View>
                <Icon
                  tvParallaxProperties={null}
                  name="arrow-forward"
                  size={30}
                  style={styles.icon}
                  onPress={() => onViewDetail(item)}
                />
              </View>
            </Pressable>
          )}
          keyExtractor={(item: NewsItem) => item.id.toString()}
        />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#333333',
  },
  scrollView: {
    marginHorizontal: 0,
    backgroundColor: '#f7f7f7',
    height: '100%',
  },
  searchWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#333333',
    paddingStart: 10,
  },
  searchBar: {
    flex: 1,
  },
  newsList: {
    margin: 15,
    marginBottom: 170,
  },
  newsItem: {
    paddingVertical: 15,
    paddingHorizontal: 30,
    marginBottom: 3,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  content: {
    width: '100%',
  },
  date: {
    color: '#333333',
    marginBottom: 5,
  },
  title: {
    fontSize: config.theme.headingFontSize,
    color: config.theme.headingTextColor,
    fontWeight: config.theme.headingFontWeight,
  },
  icon: {},
});

export default NewsScreen;
